import { useAtomValue, useSetAtom } from 'jotai';

import { Button, Chip, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import {
	allAppliedFiltersAtom,
	type AppliedFilterItem,
	colorFilterAtom,
	conditionFilterAtom,
	makeFilterAtom,
	modelFilterAtom,
	onlineSalesFilterAtom,
	publishStatusFilterAtom,
	resetAllFiltersAtom,
	updateColorFilterAtom,
	updateConditionFilterAtom,
	updateImportJobIdFilterAtom,
	updateMakeFilterAtom,
	updateModelFilterAtom,
	updateOnlineSalesFilterAtom,
	updatePublishStatusFilterAtom,
	updateVehicleCategoryFilterAtom,
	updateVehicleMatchFilterAtom,
	vehicleCategoryFilterAtom,
	vehicleMatchFilterAtom,
} from '../../atoms/inventoryAtoms';

/**
 * InventoryFiltersAppliedSection component that displays currently applied filters
 * with the ability to remove individual filters or clear all filters
 */
export function InventoryFiltersAppliedSection() {
	const allAppliedFilters = useAtomValue(allAppliedFiltersAtom);
	const clearAllFilters = useSetAtom(resetAllFiltersAtom);
	const setVehicleMatch = useSetAtom(updateVehicleMatchFilterAtom);
	const setOnlineSales = useSetAtom(updateOnlineSalesFilterAtom);
	const setPublishStatus = useSetAtom(updatePublishStatusFilterAtom);
	const setMakes = useSetAtom(updateMakeFilterAtom);
	const setModels = useSetAtom(updateModelFilterAtom);
	const setColors = useSetAtom(updateColorFilterAtom);
	const setVehicleCategories = useSetAtom(updateVehicleCategoryFilterAtom);
	const setConditions = useSetAtom(updateConditionFilterAtom);
	const setImportJobId = useSetAtom(updateImportJobIdFilterAtom);

	// Read current filter values for removal operations
	const currentVehicleMatch = useAtomValue(vehicleMatchFilterAtom);
	const currentOnlineSales = useAtomValue(onlineSalesFilterAtom);
	const currentPublishStatus = useAtomValue(publishStatusFilterAtom);
	const currentMakes = useAtomValue(makeFilterAtom);
	const currentModels = useAtomValue(modelFilterAtom);
	const currentColors = useAtomValue(colorFilterAtom);
	const currentVehicleCategories = useAtomValue(vehicleCategoryFilterAtom);
	const currentConditions = useAtomValue(conditionFilterAtom);

	// Use the applied filters from the atoms
	const allFilters: AppliedFilterItem[] = allAppliedFilters.map((filter) => ({
		...filter,
		clearFunction: getClearFunctionForFilter(filter.id),
	}));

	const totalAppliedCount = allFilters.length;

	// Don't render if no filters are applied
	if (totalAppliedCount === 0) {
		return null;
	}

	function getClearFunctionForFilter(filterId: string) {
		// Handle vehicle match filters - extract the value from the id
		if (filterId.startsWith('vehicle-match-')) {
			const value = filterId.replace('vehicle-match-', '');
			return () => {
				const updated = currentVehicleMatch.filter((v) => v !== value);
				setVehicleMatch(updated);
			};
		}

		// Handle online sales filters - extract the value from the id
		if (filterId.startsWith('online-sales-')) {
			const value = filterId.replace('online-sales-', '');
			return () => {
				const updated = currentOnlineSales.filter((v) => v !== value);
				setOnlineSales(updated);
			};
		}

		// Handle publish status filters - extract the value from the id
		if (filterId.startsWith('publish-status-')) {
			const value = filterId.replace('publish-status-', '');
			return () => {
				const updated = currentPublishStatus.filter((v) => v !== value);
				setPublishStatus(updated);
			};
		}

		// Handle make filters - extract the value from the id
		if (filterId.startsWith('make-')) {
			const value = filterId.replace('make-', '');
			return () => {
				const updated = currentMakes.filter((v) => v !== value);
				setMakes(updated);
			};
		}

		// Handle model filters - extract the value from the id
		if (filterId.startsWith('model-')) {
			const value = filterId.replace('model-', '');
			return () => {
				const updated = currentModels.filter((v) => v !== value);
				setModels(updated);
			};
		}

		// Handle color filters - extract the value from the id
		if (filterId.startsWith('color-')) {
			const value = filterId.replace('color-', '');
			return () => {
				const updated = currentColors.filter((v) => v !== value);
				setColors(updated);
			};
		}

		// Handle vehicle category filters - extract the value from the id
		if (filterId.startsWith('vehicleCategory-')) {
			const value = filterId.replace('vehicleCategory-', '');
			return () => {
				const updated = currentVehicleCategories.filter((v) => v !== value);
				setVehicleCategories(updated);
			};
		}

		// Handle condition filters - extract the value from the id
		if (filterId.startsWith('condition-')) {
			const value = filterId.replace('condition-', '');
			return () => {
				const updated = currentConditions.filter((v) => v !== value);
				setConditions(updated);
			};
		}

		// Handle single-value filters
		switch (filterId) {
			case 'importJobId':
				return () => setImportJobId(null);
			default:
				return () => {};
		}
	}

	const handleRemoveFilter = (filterId: string) => {
		const filter = allFilters.find((f) => f.id === filterId);
		if (filter) {
			filter.clearFunction();
		}
	};

	return (
		<Stack itemsSpacing='12'>
			<Group itemsAlignX='space-between' itemsAlignY='center' noWrap>
				<Text size='body' weight='semibold' align='left'>
					Applied filters ({totalAppliedCount})
				</Text>
				<Button variant='underlined' size='small' onPress={() => clearAllFilters()}>
					Clear all
				</Button>
			</Group>
			<Stack itemsSpacing='20'>
				<Group className='applied-filters-chips-group' itemsSpacing='8' itemsAlignX='start'>
					{allFilters.map((filter) => (
						<Chip
							key={filter.id}
							size='small'
							appearance='outline'
							onClose={() => handleRemoveFilter(filter.id)}>
							{filter.value}
						</Chip>
					))}
				</Group>
			</Stack>
		</Stack>
	);
}
